// Handle Vite preload errors (e.g., when a new deployment happens and old chunks are requested).
// Vite dispatches one event per failed dependency (a chunk can have several CSS/JS deps), so the
// listener must stay attached and swallow every event while the reload is pending — otherwise the
// later ones throw, reach the ErrorBoundary and get reported to Sentry.
export const VITE_PRELOAD_RELOAD_KEY = "vite:preloadError:lastReloadAt";
export const VITE_PRELOAD_RELOAD_COOLDOWN_MS = 30_000;

type VitePreloadErrorHandlerDeps = {
  /** Getter, not a value: merely accessing `window.sessionStorage` can throw when storage is blocked. */
  getStorage?: () => Storage;
  reload?: () => void;
  now?: () => number;
};

export function createVitePreloadErrorHandler({
  getStorage = () => window.sessionStorage,
  reload = () => window.location.reload(),
  now = () => Date.now(),
}: VitePreloadErrorHandlerDeps = {}) {
  // Set once a vite:preloadError triggers a page reload.
  let isReloading = false;

  const getLastReloadAt = (): number => {
    try {
      return Number(getStorage().getItem(VITE_PRELOAD_RELOAD_KEY)) || 0;
    } catch {
      return 0; // Treated as "no recent reload"; the write below then fails and skips the reload.
    }
  };

  const handleEvent = (event: Event): void => {
    if (isReloading) {
      event.preventDefault();
      return;
    }

    // A reload just happened and assets still fail: the asset is genuinely missing. Let the error
    // propagate so it surfaces (and is reported) instead of looping reloads.
    if (now() - getLastReloadAt() < VITE_PRELOAD_RELOAD_COOLDOWN_MS) {
      console.warn(
        "Vite preload error detected right after a reload attempt. Skipping reload to avoid a reload loop.",
        event,
      );
      return;
    }

    // Only reload once the cooldown is persisted; without it a genuinely missing asset would
    // reload forever, so let Vite surface the original error instead.
    try {
      getStorage().setItem(VITE_PRELOAD_RELOAD_KEY, String(now()));
    } catch {
      console.warn(
        "Vite preload error detected, but session storage is unavailable. Skipping reload to avoid a reload loop.",
        event,
      );
      return;
    }

    event.preventDefault();
    isReloading = true;
    console.warn("Vite preload error detected. Reloading page to fetch new assets...", event);
    reload();
  };

  return { handleEvent };
}
